/**
 * EKSELENS360 Maturity Engine — admin panel.
 *
 * Views in this one component (no extra routing):
 *  - "list"   :  all runs + "New assessment" CTA
 *  - "new"    :  create-run wizard (client info + framework + weighting)
 *  - "answer" :  guided indicator-by-indicator form with live radar
 *  - "view"   :  completed run summary (radar + dimension breakdown + CSI catalogue)
 *  - "compare":  multi-run radar overlay + amber-coded difference heatmap
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, ArrowLeft, Trash2, BarChart3, Scale, CheckCircle2, AlertTriangle, FileDown, FolderOpen, Lock, Sparkles, ShieldCheck, Compass, Layers, Building2 } from "lucide-react";
import api, { API_BASE } from "../../lib/api";
import DeliverableBar from "./DeliverableBar";
import RadarMaturity from "../charts/RadarMaturity";
import ArtefactsView from "./ArtefactsView";
import ValueAtStakePanel from "./ValueAtStakePanel";
import EngagementRosterPanel from "./EngagementRosterPanel";
import FinalisePanel from "./FinalisePanel";
import { OrgPicker } from "./OrgPicker";
import DecisionsActionsPanel from "./DecisionsActionsPanel";
import BlueprintAmbitionPanel from "./BlueprintAmbitionPanel";
import QAGatePanel from "./QAGatePanel";
import AssurancePanel from "./AssurancePanel";
import ChallengeLogPanel from "./ChallengeLogPanel";
import ReviewerMode from "./ReviewerMode";
import SectorOverlayChip from "./SectorOverlayChip";
import ReassessTrendPanel from "./ReassessTrendPanel";
import ClientLogoPanel from "./ClientLogoPanel";
import EvidenceSprintPanel from "./EvidenceSprintPanel";
import AiOperationsPanel from "./AiOperationsPanel";
import CohortReadiness from "./CohortReadiness";
import QualityOverview from "./QualityOverview";
import SharePanel from "./SharePanel";
import ArchivePanel from "./ArchivePanel";
import { BRAND, COMPARE_PALETTE, diffColour } from "../../data/brandColors";

const STATUS_STYLE = {
  draft: "ek-surface-2 ek-text-soft",
  in_progress: "bg-amber-50 text-amber-800",
  completed: "bg-emerald-50 text-emerald-800",
};

// Run-cockpit workspace tabs (replaces the old long right-column stack)
const RUN_TABS = [
  { id: "assessment", label: "Assessment", Icon: BarChart3 },
  { id: "ai", label: "AI Operations", Icon: Sparkles, ai: true },
  { id: "engagement", label: "Engagement & value", Icon: Compass },
  { id: "deliverables", label: "Deliverables & QA", Icon: ShieldCheck },
  { id: "reviewer", label: "Reviewer mode", Icon: Scale },
];

// Recommended sequence to reach a client-ready report. `ai:true` steps call the
// LLM and therefore incur usage charges — flagged so consultants spend deliberately.
const RUN_STEPS = [
  { n: 1, tab: "assessment", title: "Score the indicators",
    hint: "Answer every indicator with evidence, then Mark complete." },
  { n: 2, tab: "ai", ai: true, optional: true, title: "AI enrichment",
    hint: "Generate the process model, Board Q&A, plan, evidence review & CSI. Optional, but recommended before a client pack." },
  { n: 3, tab: "engagement", title: "Engagement & value",
    hint: "Set ambition, roster, value-at-stake and decisions." },
  { n: 4, tab: "deliverables", title: "QA & finalise",
    hint: "Run the QA gate, finalise, then generate the client report or pack." },
  { n: 5, tab: "reviewer", optional: true, title: "Independent review",
    hint: "Optional reviewer sign-off before you issue." },
];

function RunSequenceGuide({ activeTab, onJump, completeness, completed, finalised }) {
  const stepState = (s) => {
    if (s.tab === "assessment") return completeness >= 100 ? "done" : "active";
    if (s.tab === "engagement") return "ready"; // ambition/roster/value can be set anytime
    if (s.tab === "deliverables") return finalised ? "done" : (completed ? "ready" : "locked");
    if (s.tab === "reviewer") return finalised ? "done" : (completed ? "ready" : "locked");
    return completed ? "ready" : "locked"; // ai needs a completed run
  };
  return (
    <div className="ek-surface border ek-line p-4 mt-6" data-testid="run-sequence-guide">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] ek-text-muted">
          Recommended path to a client-ready report
        </div>
        <div className="inline-flex items-center gap-1.5 text-[10.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="ai-cost-note">
          <Sparkles size={11} /> Steps marked “AI” use the LLM and incur usage charges
        </div>
      </div>
      <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
        {RUN_STEPS.map((s) => {
          const st = stepState(s);
          const isActive = activeTab === s.tab;
          const locked = st === "locked";
          return (
            <li key={s.n}>
              <button type="button" disabled={locked} onClick={() => onJump(s.tab)}
                data-testid={`run-step-${s.tab}`}
                className={`w-full text-left border rounded-lg p-3 transition-colors h-full ${
                  isActive ? "border-[#7737F6] bg-[#7737F6]/[0.05]"
                  : locked ? "ek-line opacity-55 cursor-not-allowed"
                  : "ek-line hover:border-[#7737F6]/50"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[11px] font-semibold ${
                    st === "done" ? "bg-emerald-500 text-white" : isActive ? "bg-[#7737F6] text-white" : "ek-surface-2 ek-text-soft"}`}>
                    {st === "done" ? "✓" : s.n}
                  </span>
                  <span className="text-[12.5px] font-medium ek-text">{s.title}</span>
                  {s.ai && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[9.5px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5" data-testid="run-step-ai-badge">
                      <Sparkles size={9} /> AI
                    </span>
                  )}
                  {s.optional && !s.ai && (
                    <span className="ml-auto text-[9.5px] ek-text-muted uppercase tracking-wider">optional</span>
                  )}
                </div>
                <div className="text-[11px] ek-text-muted leading-snug">
                  {s.tab === "assessment" && st !== "done"
                    ? `${s.hint} (${completeness}% scored)` : s.hint}
                </div>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso || "—"; }
};

// ---------- export buttons (download branded outputs) ----------
const EXPORTS = [
  { fmt: "pdf", label: "Executive Assessment (PDF · full)" },
  { fmt: "execpack", label: "Executive Decision Pack (PDF · ~12pg senior-exec)" },
  { fmt: "boardpaper", label: "Board Decision Paper (PDF · 1–2pg formal submission)" },
  { fmt: "boardpack", label: "Board Decision Pack (PDF · 8–10pg read-ahead)" },
  { fmt: "pptx", label: "Workshop deck (PPTX)" },
  { fmt: "xlsx", label: "Analyst workbook (XLSX)" },
  { fmt: "docx", label: "Improvement plan (DOCX)" },
  { fmt: "blueprint", label: "Target Operating Process Blueprint (DOCX · best-in-class draft)" },
  { fmt: "wbscsv", label: "Project plan — WBS (CSV · MS Project / Smartsheet)" },
  { fmt: "wbsxlsx", label: "Project plan — WBS (XLSX · MS Project / Smartsheet)" },
  { fmt: "businesscase", label: "Business-case workbook (XLSX · value & ROI)" },
  { fmt: "benefits", label: "Benefit-realisation tracker (XLSX · client keeps)" },
  { fmt: "deliveryhub", label: "Client Delivery Hub (XLSX · scoring + prioritisation + RAID, live)" },
  { fmt: "pricing", label: "Pricing & commercials (PDF · confidential)", confidential: true },
];

function ExportBar({ runId }) {
  const base = `${API_BASE}/admin/assessments/runs/${runId}/export`;
  const PRIMARY_FMTS = ["execpack", "boardpaper", "boardpack"];
  const primary = EXPORTS.filter((e) => PRIMARY_FMTS.includes(e.fmt));
  const groups = [
    {
      id: "boardpacks", label: "Board packs — start here", tone: "primary",
      hint: "The three documents you'll reach for most. Tick and generate.",
      items: primary.map((e) => ({ id: e.fmt, label: e.label, url: `${base}/${e.fmt}` })),
    },
    {
      id: "packs", label: "Bundles (everything in one ZIP)",
      items: [
        { id: "pack", label: "Full client pack (ZIP)", note: "All client deliverables", url: `${base}/pack` },
        { id: "advisorypack", label: "Advisory pack (ZIP)", note: "Blueprint + Target Process + Project Plan + Board Q&A + Business case + Benefits tracker + Delivery Hub", url: `${base}/advisorypack` },
      ],
    },
    {
      id: "client", label: "Analyst & supporting outputs",
      items: EXPORTS.filter((e) => !e.confidential && !PRIMARY_FMTS.includes(e.fmt)).map((e) => ({ id: e.fmt, label: e.label, url: `${base}/${e.fmt}` })),
    },
    {
      id: "confidential", label: "Confidential — internal only", tone: "danger",
      hint: "Not included in the client pack — for internal use only.",
      items: EXPORTS.filter((e) => e.confidential).map((e) => ({ id: e.fmt, label: e.label, url: `${base}/${e.fmt}` })),
    },
  ];
  return (
    <DeliverableBar
      testId="export-bar"
      label="Branded deliverables"
      description="Pick exactly what you need — packs, individual client outputs or internal-only documents — and generate them in one place. Each format is fully Ekselens-branded; drafts export with whatever evidence is captured so far."
      studioTitle="Assessment deliverables"
      studioSubtitle="Tick what you want, generate, and ready items are highlighted with a re-download."
      groups={groups}
    />
  );
}

// ---------- helpers ----------
const seriesFromRun = (framework, scores, name = "Score", colour) => ({
  name,
  colour: colour || BRAND.purple,
  dimensions: framework.dimensions.map((d) => {
    const s = scores?.by_dimension?.find((x) => x.dimension_id === d.id);
    return { dim: d.short, score: s ? s.raw_average : 0 };
  }),
});

const bandFromBenchmark = (framework, benchmark, name = "Peer band") => {
  if (!benchmark) return null;
  return {
    name,
    colour: BRAND.purpleLight,
    bandFill: true,
    sampleSize: benchmark.sample_size,
    label: benchmark.label,
    dimensions: framework.dimensions.map((d) => {
      const b = benchmark.band[d.id] || { low: 0, median: 0, high: 0 };
      return { dim: d.short, low: b.low, median: b.median, high: b.high };
    }),
  };
};

// ---------- New-run wizard ----------
const PROFILE_GROUPS = [
  { label: "Identity & taxonomy", fields: [
    { k: "sector", t: "sector", label: "Sector" },
    { k: "geography", t: "enum", label: "Geography", opts: ["local", "national", "regional", "global"] },
    { k: "size_band", t: "enum", label: "Size band", opts: ["sme", "mid_market", "enterprise", "public_body"] },
  ]},
  { label: "Operating profile (ENTERPRISE360 superset — optional)", fields: [
    { k: "regulation", t: "enum", label: "Regulation", opts: ["low", "medium", "high", "critical"] },
    { k: "supply_chain_model", t: "enum", label: "Supply chain", opts: ["local", "national", "regional", "global", "vertically_integrated"] },
    { k: "customer_model", t: "enum", label: "Customer model", opts: ["b2b", "b2c", "b2b2c", "subscription", "public"] },
    { k: "product_model", t: "enum", label: "Product model", opts: ["physical_product", "digital_product", "service", "subscription", "project"] },
    { k: "operating_model", t: "enum", label: "Operating model", opts: ["centralised", "federated", "decentralised", "matrixed"] },
    { k: "ambition", t: "enum", label: "Ambition", opts: ["stabilise", "optimise", "scale", "transform"] },
    { k: "delivery_maturity", t: "enum", label: "Delivery maturity", opts: ["foundational", "scaling", "established", "advanced"] },
    { k: "annual_value_base_gbp", t: "number", label: "Annual value base (£)" },
  ]},
];

const prettifyOpt = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Module-scope so it isn't remounted on every NewRunForm render (avoids input focus loss).
function ProfileField({ f, value, onChange, sectors }) {
  const val = value ?? "";
  if (f.t === "number") {
    return <input type="number" value={val} onChange={(e) => onChange(f.k, e.target.value)}
      data-testid={`new-profile-${f.k}`} className="mt-2 w-full border ek-line px-3 py-2 text-[14px] ek-surface" placeholder="e.g. 100000000" />;
  }
  if (f.t === "sector") {
    return <select value={val} onChange={(e) => onChange(f.k, e.target.value)}
      data-testid={`new-profile-${f.k}`} className="mt-2 w-full border ek-line px-3 py-2 text-[14px] ek-surface">
      <option value="">—</option>
      {(sectors || []).map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
    </select>;
  }
  return <select value={val} onChange={(e) => onChange(f.k, e.target.value)}
    data-testid={`new-profile-${f.k}`} className="mt-2 w-full border ek-line px-3 py-2 text-[14px] ek-surface">
    <option value="">—</option>
    {f.opts.map((o) => <option key={o} value={o}>{prettifyOpt(o)}</option>)}
  </select>;
}

function NewRunForm({ frameworks, onCreated, onCancel }) {
  const [fwId, setFwId] = useState(frameworks[0]?.id || "");
  const [clientName, setClientName] = useState("");
  const [wprofile, setWprofile] = useState("manufacturing");
  const [wprofiles, setWprofiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [org, setOrg] = useState(null);
  const [meta, setMeta] = useState({ sectors: [], archetypes: [] });
  const [profile, setProfile] = useState({});   // canonical org superset fields
  const [derived, setDerived] = useState(null);  // live resolved archetype
  const [emph, setEmph] = useState(null);         // opt-in sector emphasis weighting

  const prettify = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const sectorLabel = (slug) => (meta.sectors || []).find((s) => s.slug === slug)?.label || (slug ? prettify(slug) : "");

  useEffect(() => { api.get("/admin/organisations/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);

  const selectOrg = (o) => {
    setOrg(o);
    if (o) {
      const next = {};
      ["sector", "geography", "size_band", "regulation", "supply_chain_model", "customer_model",
       "product_model", "operating_model", "ambition", "delivery_maturity", "annual_value_base_gbp"]
        .forEach((f) => { if (o[f] != null && o[f] !== "") next[f] = o[f]; });
      setProfile(next);
      if (o.legal_name && !clientName.trim()) setClientName(o.legal_name);
    }
  };

  useEffect(() => {
    if (!fwId) return;
    api.get(`/admin/assessments/frameworks/${fwId}`).then((r) => {
      setWprofiles(r.data.weighting_profiles || []);
      if (r.data.weighting_profiles?.find((p) => p.id === wprofile)) return;
      setWprofile(r.data.weighting_profiles?.[0]?.id || "default");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fwId]);

  // Live archetype resolution (same engine ENTERPRISE360 uses) as the profile fills.
  useEffect(() => {
    const filled = Object.fromEntries(Object.entries(profile).filter(([, v]) => v !== "" && v != null));
    if (Object.keys(filled).length === 0) { setDerived(null); return; }
    const t = setTimeout(() => {
      api.post("/admin/organisations/resolve", filled).then(({ data }) => setDerived(data)).catch(() => setDerived(null));
    }, 300);
    return () => clearTimeout(t);
  }, [profile]);

  const setField = (k, v) => setProfile((p) => ({ ...p, [k]: v === "" ? undefined : v }));

  // Opt-in sector-emphasis weighting: offered when the chosen sector has rules.
  useEffect(() => {
    if (!profile.sector) {
      setEmph(null);
      setWprofile((w) => (w.startsWith("sector:") ? "default" : w));
      return;
    }
    api.get("/admin/assessments/sector-emphasis", { params: { sector: profile.sector } })
      .then(({ data }) => {
        setEmph(data.available ? data : null);
        setWprofile((w) => {
          if (data.available && w.startsWith("sector:")) return data.id;
          if (!data.available && w.startsWith("sector:")) return "default";
          return w;
        });
      })
      .catch(() => setEmph(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.sector]);

  const submit = async (e) => {
    e.preventDefault();
    if (!clientName.trim()) return toast.error("Client / engagement name is required");
    setBusy(true);
    try {
      const orgProfile = Object.fromEntries(
        Object.entries(profile).filter(([, v]) => v !== "" && v != null)
          .map(([k, v]) => [k, k === "annual_value_base_gbp" ? Number(v) : v]));
      const { data } = await api.post("/admin/assessments/runs", {
        framework_id: fwId,
        client: {
          name: clientName.trim(),
          sector: sectorLabel(profile.sector) || "Other",
          size: profile.size_band ? prettify(profile.size_band) : undefined,
          region: profile.geography ? prettify(profile.geography) : undefined,
        },
        weighting_profile_id: wprofile,
        organisation_id: org?.id || null,
        org_profile: Object.keys(orgProfile).length ? orgProfile : null,
      });
      toast.success("Assessment created");
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="ek-surface border ek-line p-6 lg:p-8 max-w-3xl" data-testid="assessment-new-form">
      <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#7737F6]">EKSELENS360 · Maturity assessment</div>
      <h3 className="ek-heading text-xl ek-text mt-1">Start a new assessment</h3>
      <p className="ek-text-muted text-[13px] mt-1 max-w-2xl">Profile the client once and score the framework — the company profile is shared with ENTERPRISE360 and resolves the same archetype. Everything below the framework is optional; capture as much or as little as you have.</p>

      <div className="mt-3 flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase" data-testid="new-run-chips">
        <span className="inline-flex items-center gap-1 bg-[#7737F6]/10 text-[#7737F6] rounded-full px-3 py-1"><Layers size={12} /> {(meta.sectors || []).length} sectors</span>
        <span className="inline-flex items-center gap-1 ek-surface-2 ek-text-muted rounded-full px-3 py-1"><Building2 size={12} /> {(meta.archetypes || []).length} archetypes</span>
        {derived && (
          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 normal-case tracking-normal ${derived.archetype_matched ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "ek-surface-2 ek-text-muted"}`} data-testid="new-run-archetype">
            <Sparkles size={12} /> {derived.archetype_matched ? derived.archetype_name : "Archetype: add more detail"}
          </span>
        )}
      </div>

      <div className="mt-5">
        <OrgPicker selected={org} onSelect={selectOrg} seedName={clientName} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <label className="block">
          <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">Framework</span>
          <select value={fwId} onChange={(e) => setFwId(e.target.value)} className="mt-2 w-full border ek-line px-3 py-2 text-[14px] ek-surface" data-testid="new-framework-select">
            {frameworks.map((f) => <option key={f.id} value={f.id}>{f.sub_brand} · {f.name} (v{f.version})</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">Weighting profile</span>
          <select value={wprofile} onChange={(e) => setWprofile(e.target.value)} className="mt-2 w-full border ek-line px-3 py-2 text-[14px] ek-surface" data-testid="new-profile-select">
            {wprofiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            {emph && <option value={emph.id} data-testid="new-sector-emphasis-option">Sector emphasis — {emph.label} (×{emph.multiplier})</option>}
          </select>
          {emph && wprofile === emph.id && (
            <span className="block text-[11px] text-[#7737F6] mt-1" data-testid="new-sector-emphasis-note">{emph.rationale}</span>
          )}
        </label>
        <label className="block md:col-span-2">
          <span className="text-[11px] font-medium tracking-[0.16em] ek-text-muted uppercase">Client / engagement name</span>
          <input required value={clientName} onChange={(e) => setClientName(e.target.value)} className="mt-2 w-full border ek-line px-3 py-2 text-[14px]" placeholder="e.g. Acme Manufacturing — Q1 baseline" data-testid="new-client-name" />
        </label>
      </div>

      {PROFILE_GROUPS.map((g) => (
        <div key={g.label} className="mt-5">
          <h4 className="ek-text-soft text-[11px] uppercase tracking-[0.14em] mb-2">{g.label}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {g.fields.map((f) => (
              <label key={f.k} className="block">
                <span className="text-[11px] ek-text-muted">{f.label}</span>
                <ProfileField f={f} value={profile[f.k]} onChange={setField} sectors={meta.sectors} />
              </label>
            ))}
          </div>
        </div>
      ))}
      {org && <p className="text-[11px] ek-text-muted mt-2" data-testid="new-run-org-note">Profile saves to <span className="text-[#7737F6]">{org.legal_name}</span> and is reused everywhere.</p>}

      <div className="flex gap-3 mt-6">
        <button type="submit" disabled={busy} className="ek-btn ek-btn-primary text-[13px] disabled:opacity-60" data-testid="new-run-submit">{busy ? "Creating…" : "Create assessment"}</button>
        <button type="button" onClick={onCancel} className="ek-btn ek-btn-outline text-[13px]">Cancel</button>
      </div>
    </form>
  );
}

// ---------- Answer flow (with live radar) ----------
function PortfolioAiSpend() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/admin/assessments/ai-spend/portfolio").then(({ data }) => setD(data)).catch(() => {});
  }, []);
  if (!d || !d.runs?.length) return null;
  const t = d.totals;
  return (
    <div className="ek-surface border ek-line p-5 mb-6" data-testid="portfolio-ai-spend">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 ek-text-soft text-[11px] font-medium uppercase tracking-[0.16em]">
          <Sparkles size={14} /> AI spend across the practice
        </div>
        {t.near_or_over_cap > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="portfolio-near-cap">
            <AlertTriangle size={11} /> {t.near_or_over_cap} run(s) near or over cap
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="border ek-line rounded-lg p-3"><div className="text-[10.5px] uppercase tracking-[0.14em] ek-text-muted">Total est.</div><div className="text-2xl font-semibold text-[#7737F6] tabular-nums" data-testid="portfolio-total">${t.est_cost_usd.toFixed(2)}</div></div>
        <div className="border ek-line rounded-lg p-3"><div className="text-[10.5px] uppercase tracking-[0.14em] ek-text-muted">This month</div><div className="text-2xl font-semibold ek-text tabular-nums" data-testid="portfolio-month">${t.month_est_cost_usd.toFixed(2)}</div></div>
        <div className="border ek-line rounded-lg p-3"><div className="text-[10.5px] uppercase tracking-[0.14em] ek-text-muted">Runs with spend</div><div className="text-2xl font-semibold ek-text tabular-nums">{t.runs_with_spend}</div></div>
        <div className="border ek-line rounded-lg p-3"><div className="text-[10.5px] uppercase tracking-[0.14em] ek-text-muted">Global cap</div><div className="text-2xl font-semibold ek-text tabular-nums">{d.global_cap?.enabled ? `$${d.global_cap.cap_usd.toFixed(2)}` : "Off"}</div></div>
      </div>
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-[11px] text-[#7737F6] mt-3 hover:underline" data-testid="portfolio-toggle">
        {open ? "Hide top runs" : "Top runs by spend"}
      </button>
      {open && (
        <div className="mt-3 border ek-line rounded-lg divide-y divide-zinc-100" data-testid="portfolio-runs">
          {d.runs.slice(0, 10).map((r) => (
            <div key={r.run_id} className="flex items-center justify-between gap-3 px-3 py-2 text-[12.5px]">
              <span className="ek-text truncate flex items-center gap-2">
                {(r.over_cap || r.near_cap) && <AlertTriangle size={12} className="text-amber-500 shrink-0" />}
                {r.client_name}
              </span>
              <span className="ek-text-muted tabular-nums shrink-0">
                {r.generations} gen · ${r.est_cost_usd.toFixed(2)}
                {r.cap_usd != null && <span className={r.over_cap ? "text-amber-700" : ""}> / ${r.cap_usd.toFixed(2)}{r.cap_source !== "global" ? "*" : ""}</span>}
              </span>
            </div>
          ))}
          <div className="px-3 py-2 text-[10.5px] ek-text-muted">* run-specific cap · {d.note}</div>
        </div>
      )}
    </div>
  );
}

function AiSpendCard({ runId, refreshKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [ov, setOv] = useState(null);        // per-run override editor state
  const [editOv, setEditOv] = useState(false);
  const loadSpend = useCallback(() => {
    api.get(`/admin/assessments/runs/${runId}/ai-spend`).then(({ data }) => setData(data)).catch(() => {});
  }, [runId]);
  useEffect(() => {
    let live = true;
    api.get(`/admin/assessments/runs/${runId}/ai-spend`).then(({ data }) => { if (live) setData(data); }).catch(() => {});
    return () => { live = false; };
  }, [runId, refreshKey]);
  useEffect(() => {
    if (!editOv) return;
    api.get(`/admin/assessments/runs/${runId}/ai-budget`).then(({ data }) => {
      const o = data.override;
      setOv(o ? { mode: o.mode, cap_usd: o.cap_usd || "" } : { mode: "default", cap_usd: data.global?.cap_usd || "" });
    }).catch(() => {});
  }, [editOv, runId]);
  const saveOv = async () => {
    try {
      await api.put(`/admin/assessments/runs/${runId}/ai-budget`,
        { mode: ov.mode, cap_usd: Number(ov.cap_usd) || 0 });
      toast.success("Run budget override saved");
      setEditOv(false); loadSpend();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save override"); }
  };
  if (!data || data.generations === 0) return null;
  const src = data.budget?.source;
  return (
    <div className="ek-surface border ek-line p-5" data-testid="ai-spend-card">
      <div className="flex items-center gap-2 ek-text-soft text-[11px] font-medium uppercase tracking-[0.16em] mb-3">
        <Sparkles size={14} /> AI spend so far
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-[13px] ek-text-soft">Est. cost (indicative)</span>
        <span className="text-[#7737F6] text-2xl font-semibold tabular-nums" data-testid="ai-spend-total">${data.est_cost_usd.toFixed(2)}</span>
      </div>
      <div className="flex items-baseline justify-between mt-1">
        <span className="text-[13px] ek-text-soft">Generations</span>
        <span className="ek-text text-[15px] font-medium tabular-nums" data-testid="ai-spend-gens">{data.generations}</span>
      </div>
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-[11px] text-[#7737F6] mt-3 hover:underline" data-testid="ai-spend-toggle">
        {open ? "Hide breakdown" : "Breakdown by generator"}
      </button>
      {data.budget?.enabled && (
        <div className={`mt-3 p-2.5 border text-[11px] rounded ${data.budget.over_cap ? "bg-amber-50 border-amber-300 text-amber-900" : "ek-line ek-text-muted"}`} data-testid="ai-budget-status">
          {data.budget.over_cap ? (
            <><AlertTriangle size={12} className="inline mr-1 -mt-0.5" /> Budget cap reached (${data.budget.cap_usd.toFixed(2)}/run). New AI generations are blocked until you raise or turn off the cap.</>
          ) : (
            <>Budget cap on: ${data.budget.cap_usd.toFixed(2)}/run · ~${data.budget.remaining_usd?.toFixed(2)} left.</>
          )}
        </div>
      )}
      {src === "run-exempt" && (
        <div className="mt-2 text-[11px] ek-text-muted" data-testid="ai-budget-source">This run is exempt from the AI budget cap.</div>
      )}
      {src === "run-custom" && (
        <div className="mt-2 text-[11px] ek-text-muted" data-testid="ai-budget-source">This run uses a custom cap (${data.budget.cap_usd.toFixed(2)}), overriding the global default.</div>
      )}
      <button type="button" onClick={() => setEditOv((v) => !v)} className="text-[11px] text-[#7737F6] mt-2 hover:underline block" data-testid="ai-budget-override-toggle">
        {editOv ? "Close run cap" : "Set a cap for this run"}
      </button>
      {editOv && ov && (
        <div className="mt-2 p-3 border ek-line rounded space-y-2" data-testid="ai-budget-override-editor">
          <select value={ov.mode} onChange={(e) => setOv((s) => ({ ...s, mode: e.target.value }))}
                  className="w-full border ek-line text-[12.5px] px-2 py-1.5 ek-surface" data-testid="ai-budget-override-mode">
            <option value="default">Use global default</option>
            <option value="custom">Custom cap for this run</option>
            <option value="exempt">Exempt this run (no cap)</option>
          </select>
          {ov.mode === "custom" && (
            <div className="flex items-center gap-2">
              <span className="text-[12px] ek-text-muted">Cap $</span>
              <input type="number" min="0" step="0.5" value={ov.cap_usd} data-testid="ai-budget-override-cap"
                     onChange={(e) => setOv((s) => ({ ...s, cap_usd: e.target.value }))}
                     className="w-24 border ek-line text-[12.5px] px-2 py-1.5 ek-surface tabular-nums" />
            </div>
          )}
          <button type="button" onClick={saveOv} className="ek-btn ek-btn-outline w-full text-[12px]" data-testid="ai-budget-override-save">Save run cap</button>
        </div>
      )}
      {open && (
        <div className="mt-2 space-y-1" data-testid="ai-spend-breakdown">
          {data.breakdown.map((b) => (
            <div key={b.generator} className="flex items-center justify-between text-[11.5px]">
              <span className="ek-text-soft">{b.label}</span>
              <span className="ek-text-muted tabular-nums">{b.count} · ${b.est_cost_usd.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="text-[10.5px] ek-text-muted mt-3 leading-snug">
        Indicative from list prices (incl. regenerations). Actual spend: Profile → Universal Key.
      </div>
    </div>
  );
}


function AnswerView({ runId, onBack, onCompleted }) {
  const [run, setRun] = useState(null);
  const [framework, setFramework] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runTab, setRunTab] = useState(() => {
    try { return sessionStorage.getItem(`run-tab-${runId}`) || "assessment"; } catch { return "assessment"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(`run-tab-${runId}`, runTab); } catch { /* ignore */ }
  }, [runId, runTab]);

  const reload = useCallback(async () => {
    const { data: r } = await api.get(`/admin/assessments/runs/${runId}`);
    setRun(r);
    const { data: f } = await api.get(`/admin/assessments/frameworks/${r.framework_id}`);
    setFramework(f);
    try {
      const sector = r?.client?.sector ? encodeURIComponent(r.client.sector) : null;
      const url = sector ? `/admin/assessments/benchmark/${r.framework_id}?sector=${sector}` : `/admin/assessments/benchmark/${r.framework_id}`;
      const { data: b } = await api.get(url);
      // Prefer sector-specific band when present; else fall back to all-sector
      setBenchmark(b.sector || b.all);
    } catch {
      setBenchmark(null);
    }
  }, [runId]);

  useEffect(() => { reload(); }, [reload]);

  const setAnswer = async (indicatorId, score, evidence, confidence) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/assessments/runs/${runId}`, {
        answers: { [indicatorId]: { score, evidence: evidence || "", confidence: confidence || "med" } },
      });
      setRun(data);
    } finally { setBusy(false); }
  };

  const complete = async () => {
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/complete`);
      toast.success("Assessment completed");
      onCompleted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cannot complete");
    }
  };

  const [bpBusy, setBpBusy] = useState(false);
  const [bpDone, setBpDone] = useState(false);
  const toBlueprint = async () => {
    setBpBusy(true);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/to-blueprint`);
      setBpDone(true);
      toast.success(data.existing
        ? "Already linked to an ENTERPRISE360 engagement — find it under ENTERPRISE360 › Clients"
        : "ENTERPRISE360 engagement created & pre-filled — find it under ENTERPRISE360 › Clients");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create blueprint");
    } finally {
      setBpBusy(false);
    }
  };

  const weightingLabel = (id) => (id || "").startsWith("sector:")
    ? `Sector emphasis · ${(id.split(":")[1] || "").replace(/_/g, " ")}`
    : `${id} profile`;

  if (!run || !framework) return <p className="ek-text-muted text-[13px]">Loading…</p>;

  const series = [seriesFromRun(framework, run.scores, run.client.name)];
  const peerBand = benchmark
    ? bandFromBenchmark(framework, benchmark, `Peer band · ${run.client.sector || "all"}`)
    : null;
  const scores = run.scores || {};
  const completeness = scores.completeness_pct ?? 0;

  return (
    <div data-testid="assessment-answer-view">
      <div className="flex items-center justify-between gap-3 mb-6">
        <button type="button" onClick={onBack} className="ek-text-soft hover:text-black inline-flex items-center gap-1 text-[13px]"><ArrowLeft size={14}/> Back to assessments</button>
        <button type="button" onClick={toBlueprint} disabled={bpBusy} data-testid="run-to-blueprint-btn"
          className="ek-btn ek-btn-outline text-[13px] inline-flex items-center gap-1.5 disabled:opacity-60">
          <Compass size={14}/> {bpBusy ? "Creating…" : bpDone ? "Blueprint created ✓" : "Convert to ENTERPRISE360 blueprint"}
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div>
            <div className="ek-eyebrow text-[#7737F6]">{framework.sub_brand} · {weightingLabel(run.weighting_profile_id)}</div>
            <h3 className="ek-heading text-xl mt-2 ek-text">{run.client.name}</h3>
            <p className="ek-text-muted text-[13px] mt-1">{run.client.sector} · {run.client.size || ""} {run.client.region ? `· ${run.client.region}` : ""}</p>
            {run.organisation_archetype_name && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] px-2.5 py-1" data-testid="run-archetype-badge">
                <Sparkles size={12}/> Archetype: {run.organisation_archetype_name}
                {Array.isArray(run.organisation_overlays) && run.organisation_overlays.length > 0 && (
                  <span className="text-emerald-600"> · {run.organisation_overlays.length} overlay{run.organisation_overlays.length > 1 ? "s" : ""}</span>
                )}
              </div>
            )}
          </div>

          {run.emphasis_applied && (
            <div className="mt-4 border border-[#7737F6]/25 bg-[#7737F6]/5 rounded-lg p-3.5" data-testid="run-emphasis-panel">
              <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#7737F6]">
                <Scale size={13}/> Why these weights — {run.emphasis_applied.label} emphasis (×{run.emphasis_applied.multiplier})
              </div>
              <p className="text-[11.5px] ek-text-muted mt-1">{run.emphasis_applied.rationale}</p>
              {run.emphasis_applied.emphasised?.length > 0 ? (
                <div className="mt-2 space-y-1">
                  {run.emphasis_applied.emphasised.map((d) => (
                    <div key={d.dimension_id} className="flex items-center justify-between text-[12px] ek-text" data-testid={`emphasis-dim-${d.dimension_id}`}>
                      <span className="truncate">{d.name}</span>
                      <span className="tabular-nums ek-text-muted shrink-0">{d.from_weight} <span className="text-[#7737F6]">→ {d.to_weight}</span></span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11.5px] ek-text-muted mt-1 italic">No dimensions in this framework matched the sector emphasis — scoring is unchanged.</p>
              )}
            </div>
          )}
          {/* Sequence guide — highlights the recommended tab order and flags AI (LLM-charged) steps so spend is deliberate */}
          <RunSequenceGuide
            activeTab={runTab}
            onJump={setRunTab}
            completeness={completeness}
            completed={run.status === "completed"}
            finalised={!!run.finalised}
          />

          {/* Run workspace tabs — splits the old long right-column stack into focused views */}
          <div className="flex flex-wrap gap-1 border-b ek-line mt-6 mb-6" data-testid="run-tabs">
            {RUN_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setRunTab(t.id)}
                data-testid={`run-tab-${t.id}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-medium border-b-2 -mb-px transition-colors ${
                  runTab === t.id ? "border-[#7737F6] text-[#7737F6]" : "border-transparent ek-text-muted hover:text-zinc-800"
                }`}
              >
                <t.Icon size={14} /> {t.label}
                {t.ai && (
                  <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1 py-0.5" title="Uses AI — incurs LLM usage charges" data-testid="run-tab-ai-badge">
                    AI
                  </span>
                )}
              </button>
            ))}
          </div>

          {runTab === "assessment" && (
            <div className="space-y-8" data-testid="run-tab-panel-assessment">
              {framework.dimensions.map((dim, di) => (
                <section key={dim.id} className="ek-surface border ek-line p-5 lg:p-6" data-testid={`dim-${dim.id}`}>
                  <div className="flex items-baseline justify-between flex-wrap gap-2">
                    <h4 className="ek-heading text-base ek-text">{String(di + 1).padStart(2, "0")}. {dim.name}</h4>
                    <span className="text-[10px] tracking-[0.18em] ek-text-muted uppercase">{dim.anchor || ""}</span>
                  </div>
                  <div className="mt-4 space-y-4">
                    {dim.indicators.map((ind) => (
                      <Indicator key={ind.id} indicator={ind} answer={run.answers[ind.id]} disabled={busy} onChange={(score, evidence, confidence) => setAnswer(ind.id, score, evidence, confidence)} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}

          {runTab === "ai" && (
            <div data-testid="run-tab-panel-ai">
              <AiOperationsPanel runId={runId} />
            </div>
          )}

          {runTab === "engagement" && (
            <div className="space-y-4" data-testid="run-tab-panel-engagement">
              <BlueprintAmbitionPanel runId={runId} run={run} onChange={reload} />
              <SectorOverlayChip runId={runId} />
              <EngagementRosterPanel runId={runId} run={run} onChange={reload} />
              <ClientLogoPanel runId={runId} run={run} onChange={reload} locked={!!run.finalised} />
              {run.status === "completed" && <DecisionsActionsPanel runId={runId} refreshKey={run.finalised ? "frozen" : "live"} />}
              {run.status === "completed" && <ReassessTrendPanel runId={runId} />}
              <ValueAtStakePanel runId={runId} />
              {run.status === "completed" && <EvidenceSprintPanel runId={runId} />}
              <SharePanel runId={runId} />
            </div>
          )}

          {runTab === "deliverables" && (
            <div className="space-y-4" data-testid="run-tab-panel-deliverables">
              <ExportBar runId={runId} />
              {run.status === "completed" ? (
                <>
                  <QAGatePanel runId={runId} />
                  <FinalisePanel runId={runId} run={run} onChange={reload} />
                  <AssurancePanel runId={runId} locked={!!run.finalised} />
                  <ChallengeLogPanel runId={runId} locked={!!run.finalised} />
                </>
              ) : (
                <AssurancePanel runId={runId} locked={!!run.finalised} />
              )}
            </div>
          )}

          {runTab === "reviewer" && (
            <div data-testid="run-tab-panel-reviewer">
              {run.status === "completed" ? (
                <ReviewerMode runId={runId} />
              ) : (
                <div className="text-[13px] ek-text-muted p-5 ek-surface border ek-line">
                  Complete the assessment to open the independent reviewer view.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky HUD — radar, score, progress + quick actions (never an endless scroll) */}
        <aside className="space-y-6 lg:sticky lg:top-32 self-start" data-testid="run-hud">
          <div className="ek-surface border ek-line p-5">
            <div className="flex items-center gap-2 ek-text-soft text-[12px] font-medium uppercase tracking-[0.16em]"><BarChart3 size={14}/> Live preview</div>
            <RadarMaturity series={series} benchmark={peerBand} max={5} height={300} />
            <div className="text-[11px] ek-text-muted mt-2 text-center">Scores update as you answer</div>
          </div>
          <div className="ek-surface border ek-line p-5">
            <div className="text-[11px] font-medium uppercase tracking-[0.16em] ek-text-muted mb-3">Summary</div>
            <div className="flex items-baseline justify-between"><span className="text-[13px] ek-text-soft">Overall (weighted)</span><span className="text-[#7737F6] text-2xl font-semibold tabular-nums">{scores.overall_weighted ?? "—"}</span></div>
            <div className="flex items-baseline justify-between mt-2"><span className="text-[13px] ek-text-soft">Level</span><span className="ek-heading text-base">{scores.overall_level ?? "—"}</span></div>
            {scores.overall_capped_by ? (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 text-amber-900 text-[12px]" data-testid="capped-warning"><AlertTriangle size={13} className="inline mr-1 -mt-0.5"/> {scores.overall_capped_by}</div>
            ) : null}
            <div className="mt-4 h-2 ek-surface-2 rounded">
              <div className="h-2 bg-[#7737F6] rounded" style={{ width: `${completeness}%` }} />
            </div>
            <div className="text-[11px] ek-text-muted mt-1 text-right">{completeness}% complete</div>
            <button type="button" disabled={completeness < 100} onClick={complete} className="ek-btn ek-btn-primary w-full mt-5 text-[13px] disabled:opacity-50" data-testid="complete-run-btn"><CheckCircle2 size={13}/> Mark complete</button>
            <button type="button" onClick={() => setRunTab("deliverables")} className="ek-btn ek-btn-outline w-full mt-2 text-[13px]" data-testid="hud-generate-deliverables"><FileDown size={13}/> Generate deliverables</button>
          </div>
          <AiSpendCard runId={runId} refreshKey={runTab} />
        </aside>
      </div>
    </div>
  );
}

const Indicator = ({ indicator, answer, disabled, onChange }) => {
  const [evidence, setEvidence] = useState(answer?.evidence || "");
  const [confidence, setConfidence] = useState(answer?.confidence || "med");
  const currentScore = answer?.score;

  return (
    <div className="border ek-line p-4 bg-zinc-50/40" data-testid={`indicator-${indicator.id}`}>
      <div className="text-[14px] font-medium ek-text">{indicator.prompt}</div>
      {indicator.evidence_hint ? <div className="text-[11px] ek-text-muted mt-0.5">Evidence: {indicator.evidence_hint}</div> : null}
      <div className="mt-3 space-y-1">
        {indicator.options.map((opt) => {
          const selected = currentScore === opt.score;
          return (
            <button
              key={opt.score}
              type="button"
              disabled={disabled}
              onClick={() => onChange(opt.score, evidence, confidence)}
              data-testid={`indicator-${indicator.id}-opt-${opt.score}`}
              className={`w-full text-left flex items-start gap-3 px-3 py-2 text-[13px] transition-colors ${selected ? "bg-[#7737F6] text-white" : "ek-surface border ek-line hover:border-[#7737F6]"}`}
            >
              <span className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-semibold ${selected ? "ek-surface text-[#7737F6]" : "ek-surface-2 ek-text-soft"}`}>{opt.score}</span>
              <span className="flex-1">{opt.label}</span>
            </button>
          );
        })}
      </div>
      {currentScore ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
          <textarea
            rows={1}
            placeholder="Evidence / quote (optional)"
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
            onBlur={() => onChange(currentScore, evidence, confidence)}
            className="md:col-span-3 border ek-line px-3 py-2 text-[12px]"
            data-testid={`indicator-${indicator.id}-evidence`}
          />
          <select
            value={confidence}
            onChange={(e) => { setConfidence(e.target.value); onChange(currentScore, evidence, e.target.value); }}
            className="border ek-line px-2 py-2 text-[12px] ek-surface"
            data-testid={`indicator-${indicator.id}-confidence`}
          >
            <option value="low">Confidence: low</option>
            <option value="med">Confidence: medium</option>
            <option value="high">Confidence: high</option>
          </select>
        </div>
      ) : null}
    </div>
  );
};

// ---------- Run list ----------
function RunsList({ runs, onOpen, onCompare, onDelete, onOpenArtefacts }) {
  const [picked, setPicked] = useState([]);

  const togglePick = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  return (
    <div data-testid="assessments-list">
      {picked.length >= 2 ? (
        <div className="mb-4 flex items-center gap-3">
          <span className="text-[12px] ek-text-soft">{picked.length} runs selected</span>
          <button type="button" onClick={() => onCompare(picked)} className="ek-btn ek-btn-primary text-[12px]"><Scale size={13}/> Compare</button>
          <button type="button" onClick={() => setPicked([])} className="text-[12px] ek-text-muted hover:text-black">Clear</button>
        </div>
      ) : null}
      {runs.length === 0 ? (
        <div className="ek-surface border ek-line p-8 text-center" data-testid="no-runs">
          <p className="ek-text-soft text-[14px]">No assessments yet. Click <strong>New assessment</strong> to begin.</p>
        </div>
      ) : (
        <div className="overflow-x-auto ek-surface border ek-line">
          <table className="min-w-full text-[13px]">
            <thead className="ek-surface-2">
              <tr className="text-left ek-text-muted text-[11px] uppercase tracking-[0.12em]">
                <th className="p-3 w-8"></th>
                <th className="p-3">Client</th>
                <th className="p-3">Sub-brand</th>
                <th className="p-3">Sector</th>
                <th className="p-3">Status</th>
                <th className="p-3">Score</th>
                <th className="p-3">Level</th>
                <th className="p-3">Updated</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-t ek-line hover:bg-zinc-50/50" data-testid={`run-row-${r.id}`}>
                  <td className="p-3"><input type="checkbox" checked={picked.includes(r.id)} onChange={() => togglePick(r.id)} aria-label="select for compare" /></td>
                  <td className="p-3 font-medium"><button type="button" className="hover:text-[#7737F6]" onClick={() => onOpen(r)} data-testid={`assessment-row-open-${r.id}`}>{r.client_name}</button></td>
                  <td className="p-3"><span className="text-[#7737F6] font-semibold tracking-[0.14em] text-[11px]">{r.sub_brand}</span></td>
                  <td className="p-3 ek-text-soft">{r.sector}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] ${STATUS_STYLE[r.status] || "ek-surface-2"}`}>{(r.status || "").replace("_", " ")}</span></td>
                  <td className="p-3 tabular-nums">{r.overall_weighted ?? "—"}</td>
                  <td className="p-3">{r.overall_level ?? "—"}</td>
                  <td className="p-3 ek-text-muted">{fmtDate(r.updated_at)}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onOpenArtefacts(r)}
                        className="ek-text-muted hover:text-[#7737F6]"
                        aria-label="open artefacts"
                        title="Engagement artefacts"
                        data-testid={`open-artefacts-${r.id}`}
                      >
                        <FolderOpen size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(r.id)}
                        className="ek-text-muted hover:text-red-600"
                        aria-label="delete run"
                        data-testid={`delete-run-${r.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Comparison export helper ----------
async function downloadComparisonExport(runIds, fmt) {
  try {
    const res = await fetch(`${API_BASE}/admin/assessments/compare/export/${fmt}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ run_ids: runIds }),
    });
    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || `HTTP ${res.status}`);
    }
    const cd = res.headers.get("content-disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const filename = m ? m[1] : `comparison.${fmt}`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Comparison ${fmt.toUpperCase()} downloaded`);
  } catch (err) {
    toast.error(`Export failed: ${err.message || err}`);
  }
}

// ---------- Compare view ----------
function CompareView({ runIds, onBack }) {
  const [data, setData] = useState(null);
  const [framework, setFramework] = useState(null);
  const [benchmark, setBenchmark] = useState(null);

  useEffect(() => {
    api.post("/admin/assessments/compare", { run_ids: runIds }).then(async (r) => {
      setData(r.data);
      const fwId = r.data.runs[0].framework_id;
      const { data: f } = await api.get(`/admin/assessments/frameworks/${fwId}`);
      setFramework(f);
      try {
        const { data: b } = await api.get(`/admin/assessments/benchmark/${fwId}`);
        setBenchmark(b.all);
      } catch { setBenchmark(null); }
    }).catch((err) => toast.error(err?.response?.data?.detail || "Compare failed"));
  }, [runIds]);

  if (!data || !framework) return <p className="ek-text-muted text-[13px]">Loading…</p>;

  const series = data.runs.map((r, i) => seriesFromRun(framework, r.scores, r.client.name, COMPARE_PALETTE[i % COMPARE_PALETTE.length]));
  const peerBand = benchmark ? bandFromBenchmark(framework, benchmark, "Ekselens peer band") : null;
  const baselineRun = data.runs[0];

  return (
    <div data-testid="compare-view">
      <button type="button" onClick={onBack} className="ek-text-soft hover:text-black inline-flex items-center gap-1 text-[13px] mb-4"><ArrowLeft size={14}/> Back</button>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h3 className="ek-heading text-xl ek-text">Comparing {data.runs.length} assessments</h3>
          <p className="ek-text-muted text-[13px] mt-1">Baseline: <strong>{baselineRun.client.name}</strong>. Subsequent runs compared dimension-by-dimension.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => downloadComparisonExport(runIds, "pdf")} className="ek-btn ek-btn-primary text-[12px]" data-testid="compare-export-pdf"><FileDown size={13}/> Export PDF</button>
          <button type="button" onClick={() => downloadComparisonExport(runIds, "pptx")} className="ek-btn ek-btn-outline text-[12px]" data-testid="compare-export-pptx"><FileDown size={13}/> Export PPTX</button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        <div className="ek-surface border ek-line p-5">
          <div className="text-[11px] font-medium uppercase tracking-[0.16em] ek-text-muted mb-3">Radar overlay</div>
          <RadarMaturity series={series} benchmark={peerBand} max={5} height={400} />
        </div>
        <div className="ek-surface border ek-line p-5">
          <div className="text-[11px] font-medium uppercase tracking-[0.16em] ek-text-muted mb-3">Difference heatmap vs baseline</div>
          <div className="space-y-2">
            {data.diffs.map((d, idx) => {
              const compareRun = data.runs[idx + 1];
              return (
                <div key={d.vs_run_id}>
                  <div className="text-[12px] ek-text-soft mb-2"><span className="font-medium">{compareRun.client.name}</span> vs baseline</div>
                  <div className="grid grid-cols-7 gap-1">
                    {framework.dimensions.map((dim) => {
                      const cell = d.diff[dim.id];
                      const colour = cell ? diffColour(cell.delta) : BRAND.amber;
                      return (
                        <div key={dim.id} title={`${dim.name}: ${cell?.delta >= 0 ? "+" : ""}${cell?.delta}`} className="p-2 text-white text-[10px] text-center" style={{ backgroundColor: colour }}>
                          <div className="font-semibold">{dim.short}</div>
                          <div className="text-white/90 tabular-nums">{cell?.delta >= 0 ? "+" : ""}{cell?.delta}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-4 text-[11px]">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{backgroundColor: BRAND.green}}/> Improvement (≥ +0.3)</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{backgroundColor: BRAND.amber}}/> Unchanged</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{backgroundColor: BRAND.red}}/> Regression (≤ −0.3)</span>
          </div>
        </div>
      </div>
      <div className="ek-surface border ek-line p-5 mt-8">
        <div className="text-[11px] font-medium uppercase tracking-[0.16em] ek-text-muted mb-3">Per-dimension scores</div>
        <table className="min-w-full text-[12.5px]">
          <thead className="ek-text-muted text-left">
            <tr><th className="p-2">Dimension</th>{data.runs.map((r) => <th key={r.id} className="p-2">{r.client.name}</th>)}</tr>
          </thead>
          <tbody>
            {framework.dimensions.map((dim) => (
              <tr key={dim.id} className="border-t ek-line">
                <td className="p-2">{dim.name}</td>
                {data.runs.map((r) => {
                  const ds = r.scores.by_dimension.find((d) => d.dimension_id === dim.id);
                  return <td key={r.id} className="p-2 tabular-nums">{ds?.raw_average ?? "—"} <span className="text-[11px] ek-text-muted">({ds?.level_label})</span></td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Cross-portfolio view ----------
function PortfolioView({ clientName, onBack }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/assessments/portfolio?client_name=${encodeURIComponent(clientName)}`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || "No runs for this client"));
  }, [clientName]);
  if (!data) return <p className="ek-text-muted text-[13px]">Loading…</p>;
  const subs = Object.entries(data.by_sub_brand);
  return (
    <div data-testid="portfolio-view">
      <button type="button" onClick={onBack} className="ek-text-soft hover:text-black inline-flex items-center gap-1 text-[13px] mb-4"><ArrowLeft size={14}/> Back</button>
      <h3 className="ek-heading text-xl ek-text">Cross-portfolio view — {data.client_name}</h3>
      <p className="ek-text-muted text-[13px] mt-1">{data.total_runs} assessment{data.total_runs === 1 ? "" : "s"} across {subs.length} EKSELENS360 sub-brand{subs.length === 1 ? "" : "s"}. Useful for joined-up advisory across services.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 mt-8">
        {subs.map(([sub, runs]) => {
          const latest = runs[0];
          const score = latest?.scores?.overall_weighted;
          const lvl = latest?.scores?.overall_level;
          return (
            <div key={sub} className="ek-surface p-5">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10.5px] font-bold tracking-[0.18em] text-white bg-[#7737F6] px-2 py-0.5">{sub}</span>
                <span className="text-[10px] tracking-[0.14em] uppercase ek-text-muted">{runs.length} run{runs.length === 1 ? "" : "s"}</span>
              </div>
              <div className="text-3xl font-semibold text-[#7737F6] tabular-nums">{score ?? "—"}</div>
              <div className="text-[13px] ek-text-soft mt-1">{lvl ?? "—"}</div>
              <ul className="mt-3 space-y-1 text-[11.5px] ek-text-muted">
                {runs.map((r) => (
                  <li key={r.id}>v{r.framework_version} · {r.weighting_profile_id} · {r.status} · {(r.updated_at || "").slice(0, 10)}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Main panel ----------
export default function AssessmentsPanel() {
  const [view, setView] = useState("list");
  const [frameworks, setFrameworks] = useState([]);
  const [runs, setRuns] = useState([]);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [portfolioClient, setPortfolioClient] = useState("");
  const [portfolioInput, setPortfolioInput] = useState("");

  const reload = useCallback(async () => {
    const fw = await api.get("/admin/assessments/frameworks");
    setFrameworks(fw.data.items);
    const rs = await api.get("/admin/assessments/runs");
    setRuns(rs.data.items);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onCreated = (run) => { setCurrentRunId(run.id); setView("answer"); reload(); };
  const onOpen = (row) => { setCurrentRunId(row.id); setView("answer"); };
  const onOpenArtefacts = (row) => { setCurrentRunId(row.id); setView("artefacts"); };
  const onCompleted = () => { reload(); setView("list"); };
  const onCompare = (ids) => { setCompareIds(ids); setView("compare"); };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this assessment? This cannot be undone.")) return;
    try { await api.delete(`/admin/assessments/runs/${id}`); toast.success("Deleted"); reload(); } catch { toast.error("Delete failed"); }
  };

  const uniqueClients = useMemo(() => Array.from(new Set(runs.map((r) => r.client_name))).filter(Boolean).sort(), [runs]);

  return (
    <div data-testid="admin-assessments-panel">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Maturity assessments</h2>
          <p className="ek-text-muted text-[13px] mt-1">EKSELENS360 — versioned frameworks, scored assessments, branded outputs, cross-run comparison and cross-portfolio views.</p>
        </div>
        {view === "list" ? (
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setView("archive")} className="ek-btn ek-btn-outline text-[13px]" data-testid="open-archive-btn"><FolderOpen size={14}/> Archive</button>
            <button type="button" onClick={() => setView("new")} className="ek-btn ek-btn-primary text-[13px]" data-testid="new-assessment-btn"><Plus size={14}/> New assessment</button>
          </div>
        ) : null}
      </div>

      {view === "list" ? (
        <>
          <QualityOverview />
          <PortfolioAiSpend />
          <CohortReadiness />
          {/* Cross-portfolio search */}
          {uniqueClients.length > 0 ? (
            <div className="ek-surface-2 border ek-line p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="portfolio-search">
              <span className="text-[11.5px] font-medium tracking-[0.14em] uppercase ek-text-muted">Cross-portfolio view</span>
              <input
                list="portfolio-clients"
                value={portfolioInput}
                onChange={(e) => setPortfolioInput(e.target.value)}
                placeholder="Client name…"
                className="px-3 py-1.5 border ek-line text-[13px] flex-1 max-w-xs ek-surface"
                data-testid="portfolio-client-input"
              />
              <datalist id="portfolio-clients">
                {uniqueClients.map((c) => <option key={c} value={c} />)}
              </datalist>
              <button
                type="button"
                disabled={!portfolioInput.trim()}
                onClick={() => { setPortfolioClient(portfolioInput.trim()); setView("portfolio"); }}
                className="ek-btn ek-btn-outline text-[12px] disabled:opacity-50"
                data-testid="portfolio-go"
              >Open</button>
              <span className="text-[11px] ek-text-muted">See every sub-brand assessment for one client side-by-side.</span>
            </div>
          ) : null}
          <RunsList runs={runs} onOpen={onOpen} onCompare={onCompare} onDelete={onDelete} onOpenArtefacts={onOpenArtefacts} />
        </>
      ) : view === "new" ? (
        <NewRunForm frameworks={frameworks} onCreated={onCreated} onCancel={() => setView("list")} />
      ) : view === "answer" ? (
        <AnswerView runId={currentRunId} onBack={() => { setView("list"); reload(); }} onCompleted={onCompleted} />
      ) : view === "compare" ? (
        <CompareView runIds={compareIds} onBack={() => setView("list")} />
      ) : view === "portfolio" ? (
        <PortfolioView clientName={portfolioClient} onBack={() => setView("list")} />
      ) : view === "artefacts" ? (
        <ArtefactsView runId={currentRunId} onBack={() => { setView("list"); reload(); }} />
      ) : view === "archive" ? (
        <ArchivePanel onBack={() => { setView("list"); reload(); }} />
      ) : null}
    </div>
  );
}
